// Три букета, весь каталог, сюрприз от флориста.

import { useEffect, useState } from 'react';
import { api, type Design, type PickT, type Size } from '../lib/api.ts';
import { useApp } from '../lib/store.tsx';
import { haptic } from '../lib/tg.ts';
import { track } from '../lib/events.ts';
import { FORM_RU, GAMMA_RU, rub, tasteText } from '../lib/format.ts';

const FOR_OCCASION: Record<string, string> = { birthday: 'на день рождения', love: 'на годовщину', sorry: 'чтобы извиниться', thanks: 'чтобы сказать спасибо', just: 'просто так' };
import { BouquetArt, Flower, Icon, Sheet, Spinner, ErrorNote, TitleTop } from '../ui/kit.tsx';

/** Ответы, по которым подобрана тройка. Пока они те же — показываем ту же тройку. */
const picksKeyOf = (f: { personId: number | null; occasion: string | null; size: Size; taste: unknown }) =>
  JSON.stringify([f.personId, f.occasion, f.size, f.taste]);

export function Result() {
  const { go, flow, setFlow } = useApp();
  const gift = flow.mode === 'gift';
  const key = picksKeyOf(flow);
  const cached = flow.picksKey === key && flow.picks.length ? flow.picks : null;
  const [picks, setPicks] = useState<PickT[] | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Design | null>(null);

  const load = () => {
    setPicks(null);
    setError(null);
    api
      .recommend({ personId: flow.personId, occasion: flow.occasion ?? undefined, size: flow.size, taste: flow.taste, save: !flow.portrait })
      .then((r) => {
        track('picks_shown', { designs: r.picks.map((p) => p.design.id), again: Boolean(flow.picks.length), mode: flow.portrait ? 'portrait' : flow.mode });
        setPicks(r.picks);
        setFlow({ picks: r.picks, picksKey: key });
      })
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    if (!cached) load();
    else setPicks(cached);
  }, [key]);

  const t = tasteText(flow.taste);
  const [best, ...rest] = picks ?? [];

  return (
    <main className="screen" style={{ gap: 12 }}>
      <TitleTop title="Вот что мы подобрали" />
      <div className="card row" style={{ padding: '10px 14px', justifyContent: 'space-between' }}>
        <span className="small">
          {gift ? `${flow.relation === 'mom' ? 'Для мамы' : flow.personName ? `Подарок · ${flow.personName}` : 'Подарок'}${flow.occasion && FOR_OCCASION[flow.occasion] ? `, ${FOR_OCCASION[flow.occasion]}` : ''}${flow.portrait ? '. Подобрали по твоему описанию' : ''}` : `Твой вкус: ${t || 'подбираем по всему каталогу'}`}
        </span>
        <button type="button" className="link" style={{ fontSize: 13, fontWeight: 600, minHeight: 32, flex: 'none' }} onClick={() => go({ name: flow.portrait ? 'portrait' : gift ? 'giftHow' : 'swipes' })}>
          Изменить
        </button>
      </div>
      <ErrorNote error={error} />
      {!picks && !error && <Spinner label="Подбираем букеты" />}
      {picks && picks.length === 0 && <Empty />}
      {best && (
        <div className="card big" style={{ flex: 'none' }}>
          <button type="button" onClick={() => (haptic.tap(), setOpen(best.design))} aria-label={`Подробнее о букете «${best.design.name}»`} style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'none' }}>
            <BouquetArt design={best.design} height={150} tag="Лучшее совпадение" />
          </button>
          <div className="stack" style={{ padding: '14px 16px 16px' }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--serif)', fontSize: 24 }}>{best.design.name}</span>
              <span className="price" style={{ fontSize: 17 }}>
                {rub(best.price)} · {flow.size}
              </span>
            </div>
            <span className="small">{best.design.description}</span>
            <button type="button" className="btn sm outline" onClick={() => (haptic.tap(), setOpen(best.design))}>
              Посмотреть
            </button>
          </div>
        </div>
      )}
      {rest.length > 0 && (
        <div className="grid2">
          {rest.map((p) => (
            <button key={p.design.id} type="button" className="pcard" onClick={() => (haptic.tap(), setOpen(p.design))} style={p.role === 'bolder' ? { borderColor: 'var(--pink-line)' } : undefined}>
              <BouquetArt design={p.design} height={100} tag={p.role === 'bolder' ? 'Чуть смелее' : undefined} tagPink />
              <span className="meta">
                <span className="name">{p.design.name}</span>
                <span className="tiny">{FORM_RU[p.design.form]}</span>
                <span className="price" style={{ fontSize: 15, marginTop: 4 }}>
                  {rub(p.price)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      {picks && picks.length > 0 && (
        <button type="button" className="link" style={{ alignSelf: 'center', fontSize: 14, fontWeight: 600, minHeight: 36 }} onClick={load}>
          Показать другие три
        </button>
      )}
      {picks && picks.length > 0 && (
        <button type="button" className="promo" onClick={() => go({ name: 'surprise' })}>
          <Flower fill="#F7ACD6" center="#FCE3F0" size={30} />
          <span className="stack s6" style={{ gap: 1, flex: 1 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Сложно выбрать? Доверься флористу</span>
            <span className="tiny" style={{ color: 'var(--pink-ink-2)' }}>
              Флорист соберёт сюрприз под {gift ? 'повод и вкус' : 'твой вкус'}
            </span>
          </span>
          <Icon name="next" size={18} color="#8A2F5F" />
        </button>
      )}
      <div className="bottom" style={{ gap: 8 }}>
        <span className="row tiny" style={{ justifyContent: 'center', gap: 8, color: 'var(--ink-2)' }}>
          <Icon name="camera" size={16} width={1.8} />
          Пришлём фото до отправки, и если что-то не так, поправим
        </span>
        <button type="button" className="btn outline sm" onClick={() => go({ name: 'catalog' })}>
          <Icon name="grid" size={18} />
          Посмотреть все букеты
        </button>
      </div>
      {open && <BouquetOpened design={open} />}
      {open && <BouquetSheet design={open} onClose={() => setOpen(null)} />}
    </main>
  );
}

/** Шторка букета: картинка, состав, цены по размерам и «Выбрать этот букет». */
export function BouquetSheet({ design, onClose }: { design: Design; onClose: () => void }) {
  const { go, flow, setFlow, startFlow } = useApp();
  const sizes = design.sizes.filter((s) => s.available);
  const [size, setSize] = useState<Size>(sizes.some((s) => s.size === flow.size) ? flow.size : sizes[0]?.size ?? flow.size);
  const price = sizes.find((s) => s.size === size)?.price ?? null;
  const [form, ...composition] = (design.description || '').split(':');
  return (
    <Sheet label={`Букет «${design.name}»`} onClose={onClose}>
      <div style={{ borderRadius: 20, overflow: 'hidden' }}>
        <BouquetArt design={design} height={220} />
      </div>
      <div className="stack s6" style={{ gap: 4 }}>
        <span style={{ fontFamily: 'var(--serif)', fontSize: 26 }}>{design.name}</span>
        <span className="small">
          {FORM_RU[design.form]} · {GAMMA_RU[design.gamma]?.replace(/^./, (c) => c.toUpperCase())}
        </span>
      </div>
      {composition.length > 0 && (
        <div className="stack s6" style={{ gap: 4 }}>
          <span className="label">Состав</span>
          <span className="p" style={{ fontSize: 15 }}>
            {composition.join(':').trim().replace(/^./, (c) => c.toUpperCase())}
          </span>
          <span className="tiny">Если какого-то цветка не будет, заменим равноценным.</span>
        </div>
      )}
      {composition.length === 0 && form && <span className="p">{form}</span>}
      <div className="stack s6">
        <span className="label">Размер</span>
        <div className="grid3">
          {(['S', 'M', 'L'] as Size[]).map((s) => {
            const p = sizes.find((x) => x.size === s)?.price ?? null;
            return (
              <button key={s} type="button" className="tile" disabled={!p} aria-pressed={size === s} onClick={() => (haptic.pick(), setSize(s))} style={{ flexDirection: 'column', gap: 0, justifyContent: 'center', minHeight: 56, padding: 6, opacity: p ? 1 : 0.4 }}>
                <span style={{ fontWeight: 600 }}>{s}</span>
                <span className="tiny">{p ? rub(p) : 'нет в наличии'}</span>
              </button>
            );
          })}
        </div>
      </div>
      <button
        type="button"
        className="btn"
        disabled={!price}
        onClick={() => {
          haptic.tap();
          const pos = flow.picks.findIndex((p) => p.design.id === design.id);
          track('design_chosen', { design: design.id, position: pos >= 0 ? pos + 1 : null, size });
          if (!flow.date) startFlow('self', { size });
          setFlow({ design, surprise: false, size });
          onClose();
          go({ name: 'checkout' });
        }}
      >
        Выбрать этот букет · {rub(price)}
      </button>
      <button type="button" className="quiet" onClick={onClose}>
        Назад к букетам
      </button>
    </Sheet>
  );
}

function Empty() {
  const { go } = useApp();
  return (
    <div className="stack center" style={{ alignItems: 'center', padding: '24px 0' }}>
      <Flower fill="#F7ACD6" size={72} />
      <h2 className="h2">Под такой вкус сейчас ничего не собрать</h2>
      <p className="small">Скорее всего, нужных цветов нет в этом размере. Попробуй другой размер или доверься флористу.</p>
      <button type="button" className="btn light" style={{ alignSelf: 'stretch' }} onClick={() => go({ name: 'size' })}>
        Выбрать другой размер
      </button>
      <button type="button" className="promo" onClick={() => go({ name: 'surprise' })}>
        <span style={{ flex: 1, fontWeight: 600 }}>Доверься флористу</span>
        <Icon name="next" size={18} />
      </button>
    </div>
  );
}

const GAMMA_FILTERS = [
  ['all', 'Все'],
  ['pastel', 'Пастель'],
  ['warm', 'Тёплые'],
  ['white', 'Белые'],
  ['bright', 'Яркие'],
  ['deep', 'Глубокие'],
] as const;

export function Catalog() {
  const { go, flow } = useApp();
  const [g, setG] = useState<string>('all');
  const [size, setSize] = useState<Size>(flow.size);
  const [items, setItems] = useState<Array<{ design: Design; price: number; score: number }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Design | null>(null);

  useEffect(() => {
    setItems(null);
    api
      .ranked({ personId: flow.personId, occasion: flow.occasion ?? undefined, size, taste: flow.taste })
      .then((r) => setItems(r.items))
      .catch((e) => setError(e.message));
  }, [size]);

  const list = (items ?? []).filter((x) => g === 'all' || x.design.gamma === g);
  return (
    <main className="screen" style={{ gap: 14 }}>
      <TitleTop title="Все букеты" />
      <div className="grid3" role="group" aria-label="Размер">
        {(['S', 'M', 'L'] as Size[]).map((s) => (
          <button key={s} type="button" className="tile" aria-pressed={size === s} onClick={() => (haptic.pick(), setSize(s))} style={{ justifyContent: 'center', minHeight: 44, padding: 6, fontWeight: 600 }}>
            {s}
          </button>
        ))}
      </div>
      <p className="small" style={{ margin: 0 }}>
        Сначала те, что ближе всего по вкусу.
      </p>
      <div role="group" aria-label="Гамма" className="row" style={{ gap: 8, overflowX: 'auto', margin: '0 -20px', padding: '0 20px', scrollbarWidth: 'none' }}>
        {GAMMA_FILTERS.map(([id, label]) => (
          <button key={id} type="button" className="chip sm" style={{ flex: 'none' }} aria-pressed={g === id} onClick={() => setG(id)}>
            {label}
          </button>
        ))}
      </div>
      <ErrorNote error={error} />
      {!items && !error ? (
        <Spinner />
      ) : list.length === 0 && !error ? (
        <p className="small center">В этой гамме и размере сейчас ничего нет. Попробуй другой размер.</p>
      ) : (
        <div className="grid2">
          {list.map(({ design: d, price }) => (
            <button key={d.id} type="button" className="pcard" onClick={() => (haptic.tap(), setOpen(d))}>
              <BouquetArt design={d} height={120} />
              <span className="meta">
                <span className="name">{d.name}</span>
                <span className="tiny">{FORM_RU[d.form]}</span>
                <span className="price" style={{ fontSize: 14, marginTop: 4 }}>
                  {rub(price)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      <button type="button" className="btn butter" style={{ background: 'var(--pink)', marginTop: 'auto' }} onClick={() => go({ name: 'surprise' })}>
        Доверься флористу
      </button>
      {open && <BouquetOpened design={open} />}
      {open && <BouquetSheet design={open} onClose={() => setOpen(null)} />}
    </main>
  );
}

export const SURPRISE: Record<Size, number> = { S: 3000, M: 5000, L: 8000 };

export function Surprise() {
  const { go, flow, setFlow } = useApp();
  const [notes, setNotes] = useState('');
  const t = tasteText(flow.taste);
  return (
    <main className="screen" style={{ gap: 20 }}>
      <TitleTop title="Сюрприз от флориста" />
      <div className="row" style={{ background: 'var(--pink)', borderRadius: 24, padding: 16, gap: 14 }}>
        <Flower fill="#F7ACD6" center="#FCE3F0" size={48} />
        <p className="p" style={{ fontSize: 14, color: 'var(--pink-ink-2)' }}>
          Флорист соберёт букет из самых свежих цветов дня, под повод и вкус. Каким он будет, увидишь на фото перед отправкой.
        </p>
      </div>
      <div className="stack">
        <span className="label">{flow.mode === 'gift' ? 'Вкус' : 'Твой вкус'}</span>
        <div className="wrap">
          {(t ? t.split(', ') : ['Пока не знаем, ориентируемся на повод']).map((x) => (
            <span key={x} style={{ fontSize: 14, background: 'var(--white)', border: '1px solid var(--line)', borderRadius: 18, padding: '8px 12px' }}>
              {x}
            </span>
          ))}
          <button type="button" className="link" style={{ fontSize: 14, fontWeight: 600, minHeight: 36 }} onClick={() => go({ name: 'swipes' })}>
            Изменить
          </button>
        </div>
      </div>
      <div className="stack">
        <span className="label">Сумма</span>
        <div role="group" aria-label="Сумма сюрприза" className="grid3">
          {(['S', 'M', 'L'] as Size[]).map((s) => (
            <button key={s} type="button" className="tile" aria-pressed={flow.size === s} onClick={() => (haptic.pick(), setFlow({ size: s }))} style={{ flexDirection: 'column', gap: 2, justifyContent: 'center', padding: 8, minHeight: 60 }}>
              <span className="price" style={{ fontSize: 16 }}>
                {rub(SURPRISE[s])}
              </span>
              <span className="tiny">размер {s}</span>
            </button>
          ))}
        </div>
      </div>
      <label className="field">
        <span>
          Чего точно не надо? <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--muted)' }}>по желанию</span>
        </span>
        <input className="input" value={notes} maxLength={300} onChange={(e) => setNotes(e.target.value)} placeholder="Например: без хризантем" />
      </label>
      <div className="note">
        <Icon name="info" size={20} />
        <span>Сюрприз собираем специально для тебя, поэтому вернуть его просто потому, что не понравился, нельзя. Но перед отправкой пришлём фото, и флорист всё поправит, если попросишь.</span>
      </div>
      <div className="bottom">
        <button
          type="button"
          className="btn"
          onClick={() => {
            setFlow({ surprise: true, design: null, surpriseNotes: notes });
            track('surprise_chosen', { size: flow.size });
            go({ name: 'checkout' });
          }}
        >
          Выбрать время и адрес
        </button>
      </div>
    </main>
  );
}

/** Аналитика: какой букет открыли и каким по счёту он был в тройке. */
function BouquetOpened({ design }: { design: Design }) {
  const { flow } = useApp();
  useEffect(() => {
    const pos = flow.picks.findIndex((p) => p.design.id === design.id);
    track('design_opened', { design: design.id, position: pos >= 0 ? pos + 1 : null });
  }, [design.id]);
  return null;
}
